import {AxiosError} from 'axios'
import {useMutation, UseMutationOptions} from '@tanstack/react-query'
import {MUTATION_KEYS, QUERY_KEYS, queryClient} from '@/queryClient'
import {httpEditEvent, HttpEditEventOptions} from '@/features/agenda/http/agenda.http'

export const useEditEvent = (options?: UseMutationOptions<unknown, AxiosError, HttpEditEventOptions>) => {
    return useMutation({
        ...options,
        mutationKey: [MUTATION_KEYS.EDIT_EVENT],
        mutationFn: httpEditEvent,
        onSuccess: async (data, variables, context) => {
            for (const event of data) {
                // Cancel the in-flight detail fetch for the edited event before the broad invalidation
                // below re-triggers it, so a stale in-flight response cannot overwrite the fresh data.
                await queryClient.cancelQueries({queryKey: [QUERY_KEYS.AGENDA_EVENTS, 'extended', event.id]})
                for (const patient of event.eventPatients ?? []) {
                    void queryClient.cancelQueries({queryKey: [QUERY_KEYS.PATIENTS, patient.patientId]})
                }
            }

            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ACCESSES]})
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PLANNED_ACCESSES]})
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.AGENDA_AVAILABILITIES]})
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT_ACCESS_COUNTERS]})
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.AGENDA_EVENTS_COUNT]})
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECTS]})
            // Invalidate the whole AGENDA_EVENTS tree so the edited event is refreshed everywhere it is
            // shown: the calendar list ([AGENDA_EVENTS, {therapists, patients, startDate, endDate, ...}])
            // and the per-therapist lists, the open event detail ([AGENDA_EVENTS, 'extended', id]) and the
            // project warning banner ([AGENDA_EVENTS, 'project-warning-banner']). The previous
            // 'extended'-only invalidation never matched the calendar list keys.
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.AGENDA_EVENTS]})

            for (const event of data) {
                for (const patient of event.eventPatients ?? []) {
                    void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT, patient.projectId]})
                }
            }

            options?.onSuccess?.(data, variables, context)
        }
    })
}
